import { useEffect, useState } from 'react'
import { referralApi } from '../../api/referralApi'
import { Referral } from '../../types'
import { toast } from 'react-hot-toast'
import { Loader2, ExternalLink, Copy } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { clsx } from 'clsx'

const STATUS_STYLE: Record<string, string> = {
  REFERRED: 'badge bg-blue-50 text-blue-700',
  ACKNOWLEDGED: 'badge bg-purple-50 text-purple-700',
  HIRED: 'badge bg-green-50 text-green-700',
  CLOSED: 'badge bg-gray-100 text-gray-600',
}

export default function MyReferralsPage() {
  const [referrals, setReferrals] = useState<Referral[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    referralApi
      .getMyReferrals()
      .then(({ data }) => setReferrals((data.data as any)?.content || data.data || []))
      .catch(() => toast.error('Failed to load referrals'))
      .finally(() => setLoading(false))
  }, [])

  const copyLink = (link: string) => {
    navigator.clipboard.writeText(link)
    toast.success('Referral link copied!')
  }

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 size={28} className="animate-spin text-primary-600" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-xl font-bold text-gray-900 mb-6">My Referrals</h1>

      {referrals.length === 0 ? (
        <div className="card text-center py-16 text-gray-400">No referrals yet.</div>
      ) : (
        <div className="space-y-4">
          {referrals.map((ref) => (
            <div key={ref.id} className="card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-gray-900">{ref.jobPost.title}</p>
                  <p className="text-sm text-gray-500">{ref.jobPost.company.name}</p>
                </div>
                <span className={clsx(STATUS_STYLE[ref.status])}>{ref.status}</span>
              </div>

              {/* Referral ID */}
              <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                <p className="text-xs text-gray-400 mb-1">Your Referral ID</p>
                <p className="font-mono text-sm text-gray-900 font-medium">{ref.referralId}</p>
              </div>

              {/* Referral Link */}
              <div className="flex items-center gap-2 mt-2">
                <a
                  href={ref.referralLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:underline flex items-center gap-1 truncate"
                >
                  <ExternalLink size={14} /> View application portal
                </a>
                <button
                  onClick={() => copyLink(ref.referralLink)}
                  className="btn-secondary text-xs py-1 px-2 flex items-center gap-1 shrink-0"
                >
                  <Copy size={12} /> Copy
                </button>
              </div>

              <div className="flex items-center justify-between mt-3 text-xs text-gray-400">
                <span>Referred {formatDistanceToNow(new Date(ref.createdAt), { addSuffix: true })}</span>
                {ref.referralFeeCredited && (
                  <span className="text-green-600 font-medium">
                    ₹{ref.feeCreditAmountPaise / 100} earned
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
